import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import _get from 'lodash/get';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import CardModule from '../../shared/card-module';
import Icon from '../../../atoms/icon';
import format from '../../../../utils/format';
import styles from './morningstar-facts-ratings.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';

const className = augmentClassName(styles);

const MorningstarFactsRatings = ({ facts = {}, heading }) => {
  const investmentStyleEquities = _get(facts, 'investmentStyle.equities');
  const investmentStyleFixedIncome = _get(facts, 'investmentStyle.fixedIncome');

  return (
    <CardModule
      data-analytics-componenttype="twothirds-width-component"
      data-analytics-id="TTW_MorningstarFactsRatings"
      data-analytics-section="component"
    >
      <CardModule.Header>
        {heading}
        <CardModule.AsOfDate>
          {format.date(facts.asOfDate)}
        </CardModule.AsOfDate>
      </CardModule.Header>

      <CardModule.Body>
        <Container>
          <Row>
            <Col md={6} {...className('vertical-divider', 'col-print-6')}>
              <FactRow heading={'Category'}>
                <span {...className('data-point')}>{facts.category}</span>
              </FactRow>
              <FactRow heading={'Investment Style'}>
                <div {...className('data-point')}>
                  {investmentStyleEquities && investmentStyleFixedIncome && (
                    <>
                      <div>{investmentStyleEquities}</div>
                      &nbsp;
                      <div>{investmentStyleFixedIncome}</div>
                    </>
                  )}
                  {investmentStyleEquities && !investmentStyleFixedIncome && (
                    <div>{investmentStyleEquities}</div>
                  )}
                  {!investmentStyleEquities && investmentStyleFixedIncome && (
                    <div>{investmentStyleFixedIncome}</div>
                  )}
                  {!investmentStyleEquities && !investmentStyleFixedIncome && (
                    <div {...className('n-a-text')}>N/A</div>
                  )}
                </div>
              </FactRow>
              <FactRow
                heading={'Returns vs Category'}
                subHeading={
                  typeof facts.returnVsCategory === 'number' &&
                  facts.returnVsCategory > 0
                    ? '3 Year Average'
                    : ''
                }
              >
                {
                  /* eslint-disable indent */
                  typeof facts.returnVsCategory === 'number' &&
                  facts.returnVsCategory > 0 ? (
                    <ReturnVsCategory rating={facts.returnVsCategory} />
                  ) : (
                    <div {...className('n-a-text')}>N/A</div>
                  )
                  /* eslint-enable indent */
                }
              </FactRow>
            </Col>
            <Col md={6} {...className('vertical-divider', 'col-print-6')}>
              <RatingRow
                heading={'Overall'}
                outOfFunds={facts.ratingOverallOutOf}
                rating={facts.ratingOverall}
              />
              <RatingRow
                heading={'3 Year'}
                outOfFunds={facts.rating3YearOutOf}
                rating={facts.rating3Year}
              />
              <RatingRow
                heading={'5 Year'}
                outOfFunds={facts.rating5YearOutOf}
                rating={facts.rating5Year}
              />
              <RatingRow
                heading={'10 Year'}
                outOfFunds={facts.rating10YearOutOf}
                rating={facts.rating10Year}
              />
            </Col>
          </Row>
        </Container>
      </CardModule.Body>

      <CardModule.Footer {...className('footer')}>
        <Container>
          <Row>
            <Col {...className('paragraph-standard')}>
              {facts.closeProximityDisclosure}
            </Col>
          </Row>
          <Row>
            <Col {...className('text-right')}>
              <ButtonDisclosures module={'morningstar-facts-ratings'} />
            </Col>
          </Row>
        </Container>
      </CardModule.Footer>
    </CardModule>
  );
};

MorningstarFactsRatings.propTypes = {
  facts: PropTypes.shape({
    asOfDate: PropTypes.string,
    category: PropTypes.string,
    disclosure: PropTypes.string,
    investmentStyle: PropTypes.object,
    rating10Year: PropTypes.number,
    rating10YearOutOf: PropTypes.number,
    rating3Year: PropTypes.number,
    rating3YearOutOf: PropTypes.number,
    rating5Year: PropTypes.number,
    rating5YearOutOf: PropTypes.number,
    ratingOverall: PropTypes.number,
    ratingOverallOutOf: PropTypes.number,
    returnVsCategory: PropTypes.number
  }),
  heading: PropTypes.string
};

const FactRow = ({ children, heading, subHeading }) => {
  return (
    <Row {...className('fact-row')}>
      <Col {...className('fact-col')}>
        <dl>
          <dt>
            {heading}
            {subHeading ? (
              <span {...className('sub-heading')}>{subHeading}</span>
            ) : undefined}
          </dt>
          <dd>{children}</dd>
        </dl>
      </Col>
    </Row>
  );
};

FactRow.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  heading: PropTypes.string,
  subHeading: PropTypes.string
};

const RatingRow = ({ heading, outOfFunds, rating }) => {
  const hasRating = typeof rating === 'number';
  return (
    <Row {...className('ratings-row')}>
      <Col {...className('ratings-col')}>
        <dl>
          <dt>{heading}</dt>
          <dd>
            {
              /* eslint-disable indent */
              hasRating
                ? `Out of ${format.number(outOfFunds, {
                    formatString: '0,0'
                  })} Funds`
                : ''
              /* eslint-enable indent */
            }
          </dd>
        </dl>
      </Col>
      <Col {...className('ratings-col')}>
        <span {...className('sr-only')}>
          {rating >= 1 && rating <= 5
            ? `${format.number(rating, { formatString: '0' })} out of 5 stars`
            : 'Rating Unavailable'}
        </span>
        {hasRating ? (
          <span aria-hidden="true" {...className('stars')}>
            {[1, 2, 3, 4, 5].map(ordinal => (
              <Icon.Star
                key={ordinal}
                role="img"
                aria-label="Star"
                {...className(ordinal > rating && 'text-light', ordinal > rating && 'opacity-50', 'mr-2')}
              />
            ))}
          </span>
        ) : (
          <span {...className('n-a-text')}>N/A</span>
        )}
      </Col>
    </Row>
  );
};

RatingRow.propTypes = {
  heading: PropTypes.string,
  outOfFunds: PropTypes.number,
  rating: PropTypes.oneOf([0, 1, 2, 3, 4, 5])
};

const ReturnVsCategory = ({ rating = 0 }) => {
  let srRating = 'Rating Unavailable';

  switch (rating) {
    case 1:
      srRating = 'Low';
      break;
    case 2:
      srRating = 'Below Average';
      break;
    case 3:
      srRating = 'Average';
      break;
    case 4:
      srRating = 'Above Average';
      break;
    case 5:
      srRating = 'High';
      break;
    default:
  }

  return (
    <>
      <span {...className('sr-only')}>{srRating}</span>
      <span aria-hidden="true">
        <Container fluid>
          <Row>
            {[1, 2, 3, 4, 5].map(ordinal => (
              <Col
                key={ordinal}
                {...className('rating-bar', ordinal > rating && 'inactive')}
              ></Col>
            ))}
          </Row>
          <Row {...className('d-flex', 'justify-content-between')}>
            <Col {...className('rating-bar-label')}>LOW</Col>
            <Col {...className('rating-bar-label', 'text-center')}>AVG</Col>
            <Col {...className('rating-bar-label', 'text-right')}>HIGH</Col>
          </Row>
        </Container>
      </span>
    </>
  );
};

ReturnVsCategory.propTypes = {
  rating: PropTypes.oneOf([1, 2, 3, 4, 5])
};

export default MorningstarFactsRatings;
